import { useCallback, useEffect, useRef } from 'react';

import { MessageHelper } from '../../util/message-helper';
import { readReelsUrl, reelsGalleryUrl, writeReelsUrl } from './reels-url';

/**
 * The address bar while a feed is open. Driven entirely by data-qplayer-reels-url.
 *
 * WARNING, not fixable here: the SITE has to serve borsen.dk/reel/PL/ME for a refresh to work.
 */
const useReelsRoute = ({ pattern, galleryUrl, isOpen, playlistId, mediaId, qplayerId, onOpenClip, onClose }) => {
	// A link opened cold pushed nothing, which decides whether closing steps back or rewrites.
	const pushedRef = useRef(false);
	// So popstate can tell a step back from the clip already on screen.
	const currentMediaIdRef = useRef(null);
	// Set when the browser took us out of the feed: closing must not step back a second time.
	const closedByHistoryRef = useRef(false);

	const latest = useRef({});
	latest.current = { pattern, galleryUrl, playlistId, qplayerId, onOpenClip, onClose };

	// Opening, and closing again. One effect: the cleanup IS the close.
	useEffect(() => {
		if (!isOpen || !pattern) {
			return undefined;
		}

		// The one asked for, else the one the address names, else none.
		const openingMediaId = mediaId || readReelsUrl(pattern)?.mediaId || null;
		currentMediaIdRef.current = openingMediaId;

		const address = writeReelsUrl(pattern, { playlistId, mediaId: openingMediaId });
		const current = window.location.pathname + window.location.search + window.location.hash;

		// A link opened cold is already at the clip's address; writing it again would stack a second
		// copy of this page and closing would step back onto itself.
		pushedRef.current = address !== current;
		if (pushedRef.current) {
			// Opening the feed is the one step Back should undo.
			window.history.pushState(null, '', address);
		}

		return () => {
			if (closedByHistoryRef.current) {
				// The browser already moved the address; stepping back again would leave the page.
				closedByHistoryRef.current = false;
				return;
			}

			const toGallery = () => window.history.replaceState(null, '', reelsGalleryUrl(pattern, galleryUrl));

			if (!pushedRef.current) {
				// Opened cold: nothing behind this entry, so the gallery address replaces it.
				toGallery();
				return;
			}

			// One step back is the gallery only if the viewer never used Back or Forward inside the
			// feed, so the result is verified rather than assumed. back() is async: wait for the
			// popstate it causes, with a generous timer as the backstop for a no-op step.
			let timer = null;
			const settle = () => {
				window.removeEventListener('popstate', settle);
				clearTimeout(timer);
				if (readReelsUrl(pattern)) {
					toGallery();
				}
			};
			window.addEventListener('popstate', settle);
			timer = setTimeout(settle, 600);
			window.history.back();
		};
		// mediaId is deliberately not a dependency: it is the clip to OPEN at, and re-running this
		// on every scroll would push a history entry per clip. Scrolling is handled below.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, pattern, galleryUrl, playlistId]);

	// Scrolling. Follows storyClipChanged, because every slide is its own single-media player and
	// GoBrain's queue - and with it queueItem - never runs.
	useEffect(() => {
		if (!isOpen || !pattern) {
			return undefined;
		}
		const handleMessage = (event) => {
			const data = event.data;
			if (data?.identifier !== MessageHelper.MESSAGE_BASE_IDENTIFIERS.identifier) {
				return;
			}
			if (data.type !== MessageHelper.MESSAGE_TYPES.storyClipChanged) {
				return;
			}
			// Only this feed's own player: a page can hold a second gallery, or an article player.
			const ownId = latest.current.qplayerId;
			if (ownId && data.qplayerId && data.qplayerId !== ownId) {
				return;
			}
			// An ad slot must never reach the address bar: shareable links to an advertisement, and ad
			// views counted as editorial page views.
			if (data.metadata?.slotType !== 'video' || !data.mediaId) {
				return;
			}
			if (data.mediaId === currentMediaIdRef.current) {
				return;
			}
			currentMediaIdRef.current = data.mediaId;
			// replaceState: one feed is one history entry however far it is scrolled.
			window.history.replaceState(
				null,
				'',
				writeReelsUrl(pattern, {
					playlistId: data.metadata?.playlistId || latest.current.playlistId,
					mediaId: data.mediaId,
				})
			);
		};
		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [isOpen, pattern]);

	// Back and Forward - steps the script did not create.
	const handlePopState = useCallback(() => {
		const { pattern: p, onOpenClip: open, onClose: close } = latest.current;
		if (!p) {
			return;
		}
		const clip = readReelsUrl(p);
		if (!clip) {
			// Back, out of the feed: the address is already the gallery's.
			closedByHistoryRef.current = true;
			currentMediaIdRef.current = null;
			close?.();
			return;
		}
		if (clip.mediaId && clip.mediaId !== currentMediaIdRef.current) {
			// Back into an earlier clip, or forward into a later one.
			currentMediaIdRef.current = clip.mediaId;
			open?.(clip);
		}
	}, []);

	useEffect(() => {
		if (!isOpen || !pattern) {
			return undefined;
		}
		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [isOpen, pattern, handlePopState]);
};

export default useReelsRoute;
